import { useStore } from 'app/context';
import { UserModel } from 'app/models/core/user.model';
import { BnrActionCountRow } from 'app/stores/admin.log.store';
import { isRelevantUser } from './UsageAnmeldequoteShared';
import { UsageMultiRows, UsageSlide, UsageSource, VG_NORD, VG_SUED } from './UsageReportShared';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

// Aufrufe je Jahr und VD, erstes Berichtsjahr wie im Quartalsbericht
const FIRST_YEAR = 2022;

interface YearVdCounts {
	year: number;
	label: string;
	byVd: Map<string, number>;
}

const aggregateByVd = (users: UserModel[], counts: BnrActionCountRow[]): Map<string, number> => {
	const byBnr = new Map(counts.map((c) => [c.bnr_id, c.action_count]));
	const byVd = new Map<string, number>();
	users.forEach((u) => {
		if (!isRelevantUser(u) || !u.firstVd) {
			return;
		}
		byVd.set(u.firstVd, (byVd.get(u.firstVd) || 0) + (byBnr.get(u.bnrId) || 0));
	});
	return byVd;
};

const EntwicklungSlide = (props: { title: string; vds: string[]; years: YearVdCounts[] }) => {
	const vds = props.vds.filter((vd) => props.years.some((y) => (y.byVd.get(vd) || 0) > 0));
	if (vds.length === 0) {
		return null;
	}
	const series = props.years.map((y) => ({
		name: y.label,
		data: vds.map((vd) => y.byVd.get(vd) || 0),
	}));

	return (
		<UsageSlide title={props.title} source={UsageSource.dvs}>
			<UsageMultiRows categories={vds} series={series} formatValue={formatNum} />
		</UsageSlide>
	);
};

// Folien "Entwicklung Aufrufe VG Nord/Süd": Aufrufe je VD über die Jahre.
// Jahre ohne Daten (Navigation wird erst seit dem 19.3.2026 getrackt) werden ausgeblendet.
export const UsageEntwicklungCharts = observer(() => {
	const { adminLogStore, adminUserStore } = useStore();
	const [years, setYears] = useState<YearVdCounts[]>([]);
	const [loading, setLoading] = useState(true);

	const currYear = moment().year();

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			const allYears: number[] = [];
			for (let y = FIRST_YEAR; y <= currYear; y++) {
				allYears.push(y);
			}
			const [users, ...counts] = await Promise.all([
				adminUserStore.findAllActiveUsers(),
				...allYears.map((y) => adminLogStore.loadActionCountsByBnrId('nav', `${y}-01-01`, `${y + 1}-01-01`)),
			]);

			const result: YearVdCounts[] = allYears.map((year, idx) => ({
				year,
				label: year === currYear ? `(1-${moment().month() + 1}) ${year}` : `${year}`,
				byVd: aggregateByVd(users as UserModel[], counts[idx] as BnrActionCountRow[]),
			}));
			// Jahre ohne jegliche Daten ausblenden
			setYears(result.filter((y) => Array.from(y.byVd.values()).some((v) => v > 0)));
			setLoading(false);
		};
		load();
	}, [adminLogStore, adminUserStore, currYear]);

	if (loading) {
		return <div>Wird geladen...</div>;
	}
	if (years.length === 0) {
		return null;
	}

	const range = years.length > 1 ? `${years[0].year} - ${moment().format('MM/YYYY')}` : `${moment().format('MM/YYYY')}`;

	return (
		<>
			<EntwicklungSlide title={`Entwicklung Aufrufe VG Nord – ${range}`} vds={VG_NORD} years={years} />
			<EntwicklungSlide title={`Entwicklung Aufrufe VG Süd – ${range}`} vds={VG_SUED} years={years} />
		</>
	);
});
